// Keeps a signed-in device honest: records it, notices real activity, and ends
// the sign-in when it should end. See src/lib/deviceSession.ts for the rules.

import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAmIDsl } from "@/hooks/useDsl";
import {
  clearSessionTracking,
  computeExpiry,
  describeDevice,
  getDeviceChoice,
  getDeviceId,
  getLastActivity,
  getLocalExpiry,
  getSessionStart,
  isStaleSignal,
  markActivity,
  markSessionStart,
  sessionEndReason,
  setLocalExpiry,
} from "@/lib/deviceSession";

const CHECK_INTERVAL_MS = 60_000;
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 60_000;

const MESSAGES = {
  expired: "You have been signed out. Please log in again.",
  idle: "You were signed out after 8 hours of inactivity. Please log in again.",
  revoked: "This device was signed out. Please log in again.",
};

async function endSession(reason: keyof typeof MESSAGES) {
  clearSessionTracking();
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    /* ignore, we are leaving anyway */
  }
  window.location.href = `/login?message=${encodeURIComponent(MESSAGES[reason])}`;
}

export function useSessionGuard() {
  const { user, role, session } = useAuth();
  const { isDsl, isLoading: dslLoading } = useAmIDsl();
  const registered = useRef(false);
  const lastActivityWrite = useRef(0);
  // Until this sign-in's own device row is written, the row in the database may
  // still describe a previous sign-in, so we must not act on it.
  const [deviceRowFresh, setDeviceRowFresh] = useState(false);

  // Record (or refresh) this device against the account.
  useEffect(() => {
    if (!user || !session || dslLoading || registered.current) return;
    const choice = getDeviceChoice();
    if (!choice) return; // the device question has not been answered yet

    registered.current = true;

    (async () => {
      const deviceId = getDeviceId();
      if (getSessionStart() == null) markSessionStart();
      const existing = getLocalExpiry();
      const expiresAt = existing && existing.getTime() > Date.now()
        ? existing
        : computeExpiry(choice, role, isDsl);
      setLocalExpiry(expiresAt);
      markActivity();

      let nurseryId: string | null = null;
      try {
        const { data } = await supabase
          .from("profiles")
          .select("nursery_id")
          .eq("user_id", user.id)
          .maybeSingle();
        nurseryId = data?.nursery_id ?? null;
      } catch {
        /* best effort */
      }

      try {
        await supabase.from("user_sessions").upsert(
          {
            user_id: user.id,
            nursery_id: nurseryId,
            device_id: deviceId,
            device_label: describeDevice(),
            device_choice: choice,
            expires_at: expiresAt.toISOString(),
            last_active_at: new Date().toISOString(),
            revoked_at: null,
            revoked_by: null,
          } as never,
          { onConflict: "user_id,device_id" }
        );
      } catch {
        /* never block the app on bookkeeping */
      } finally {
        setDeviceRowFresh(true);
      }
    })();
  }, [user, session, role, isDsl, dslLoading]);


  // Notice genuine activity (this resets the 8-hour idle timer).
  useEffect(() => {
    if (!user) return;
    const events = ["pointerdown", "keydown", "wheel", "touchstart", "visibilitychange"];
    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      markActivity();
      const now = Date.now();
      if (now - lastActivityWrite.current > ACTIVITY_WRITE_INTERVAL_MS) {
        lastActivityWrite.current = now;
        supabase
          .from("user_sessions")
          .update({ last_active_at: new Date().toISOString() } as never)
          .eq("user_id", user.id)
          .eq("device_id", getDeviceId())
          .then(() => undefined, () => undefined);
      }
    };
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, onActivity));
  }, [user]);

  // Check every minute: out of time, left idle, or signed out from elsewhere.
  useEffect(() => {
    if (!user || !session) return;
    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      const sessionStart = getSessionStart();
      const localExpiry = getLocalExpiry();
      // A leftover end time from an earlier sign-in on this browser must never
      // end the sign-in that has just started.
      const localReason = sessionEndReason(
        isStaleSignal(localExpiry, sessionStart) ? null : localExpiry,
        getLastActivity()
      );
      if (localReason) {
        await endSession(localReason);
        return;
      }
      // Wait until this sign-in's own device row has been saved, otherwise we
      // may be reading the previous sign-in's end time or sign-out.
      if (!deviceRowFresh) return;
      try {
        const { data } = await supabase
          .from("user_sessions")
          .select("expires_at, revoked_at")
          .eq("user_id", user.id)
          .eq("device_id", getDeviceId())
          .maybeSingle();
        if (cancelled || !data) return;
        const revokedAt = data.revoked_at ? new Date(data.revoked_at) : null;
        if (revokedAt && !isStaleSignal(revokedAt, sessionStart)) {
          await endSession("revoked");
          return;
        }
        const serverExpiry = data.expires_at ? new Date(data.expires_at) : null;
        if (serverExpiry && !isStaleSignal(serverExpiry, sessionStart)) {
          setLocalExpiry(serverExpiry);
          if (Date.now() >= serverExpiry.getTime()) await endSession("expired");
        }
      } catch {
        /* offline or blocked, local rules still apply */
      }
    };

    check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user, session, deviceRowFresh]);
}
